import { useState, useMemo, useCallback } from 'react';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Check,
  Minus,
} from 'lucide-react';
import { EmptyState, TableSkeleton } from '../common';

// Column definition types
export interface Column<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerClassName?: string;
  cell?: (row: T, index: number) => React.ReactNode;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface DataTableProps<T extends { id: string | number }> {
  data: T[];
  columns: Column<T>[];
  // Loading & empty states
  isLoading?: boolean;
  loadingRows?: number;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: React.ReactNode;
  emptyAction?: {
    label: string;
    onClick: () => void;
  };
  // Sorting
  sortable?: boolean;
  defaultSort?: SortState;
  onSort?: (sort: SortState) => void;
  manualSorting?: boolean; // If true, sorting is handled externally
  // Selection
  selectable?: boolean;
  selectedIds?: Set<string | number>;
  onSelectionChange?: (selectedIds: Set<string | number>) => void;
  // Row actions
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T, index: number) => string;
  // Styling
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  stickyHeader?: boolean;
}

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  isLoading = false,
  loadingRows = 5,
  emptyTitle = 'No data found',
  emptyDescription = 'There are no items to display.',
  emptyIcon,
  emptyAction,
  sortable = true,
  defaultSort = { column: null, direction: null },
  onSort,
  manualSorting = false,
  selectable = false,
  selectedIds: controlledSelectedIds,
  onSelectionChange,
  onRowClick,
  rowClassName,
  className = '',
  striped = false,
  hoverable = true,
  compact = false,
  stickyHeader = false,
}: DataTableProps<T>) {
  // Internal sort state (used when not controlled externally)
  const [internalSort, setInternalSort] = useState<SortState>(defaultSort);
  const sortState = onSort ? defaultSort : internalSort;

  // Internal selection state (used when not controlled externally)
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string | number>>(new Set());
  const selectedIds = controlledSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;

  // Handle sort toggle
  const handleSort = useCallback(
    (columnId: string) => {
      const newSort: SortState = {
        column: columnId,
        direction:
          sortState.column === columnId
            ? sortState.direction === 'asc'
              ? 'desc'
              : sortState.direction === 'desc'
              ? null
              : 'asc'
            : 'asc',
      };

      if (newSort.direction === null) {
        newSort.column = null;
      }

      if (onSort) {
        onSort(newSort);
      } else {
        setInternalSort(newSort);
      }
    },
    [sortState, onSort]
  );

  // Sort data if not manual
  const sortedData = useMemo(() => {
    if (manualSorting || !sortState.column || !sortState.direction) {
      return data;
    }

    const column = columns.find((c) => c.id === sortState.column);
    if (!column) return data;

    return [...data].sort((a, b) => {
      let aValue: unknown;
      let bValue: unknown;

      if (typeof column.accessor === 'function') {
        aValue = column.accessor(a);
        bValue = column.accessor(b);
      } else {
        aValue = a[column.accessor];
        bValue = b[column.accessor];
      }

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortState.direction === 'desc' ? -comparison : comparison;
    });
  }, [data, sortState, columns, manualSorting]);

  // Selection handlers
  const allSelected = data.length > 0 && data.every((row) => selectedIds.has(row.id));
  const someSelected = data.some((row) => selectedIds.has(row.id)) && !allSelected;

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map((row) => row.id)));
    }
  }, [allSelected, data, setSelectedIds]);

  const handleSelectRow = useCallback(
    (id: string | number, event?: React.MouseEvent) => {
      event?.stopPropagation();
      const newSelectedIds = new Set(selectedIds);
      if (newSelectedIds.has(id)) {
        newSelectedIds.delete(id);
      } else {
        newSelectedIds.add(id);
      }
      setSelectedIds(newSelectedIds);
    },
    [selectedIds, setSelectedIds]
  );

  // Get cell value
  const getCellValue = (row: T, column: Column<T>, index: number): React.ReactNode => {
    if (column.cell) {
      return column.cell(row, index);
    }
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    const value = row[column.accessor];
    return value as React.ReactNode;
  };

  // Render sort icon
  const renderSortIcon = (column: Column<T>) => {
    if (!sortable || column.sortable === false) {
      return null;
    }

    const isSorted = sortState.column === column.id;
    const direction = isSorted ? sortState.direction : null;

    return (
      <span className="ml-1 inline-flex">
        {direction === 'asc' ? (
          <ChevronUp className="h-4 w-4 text-cyan-400" />
        ) : direction === 'desc' ? (
          <ChevronDown className="h-4 w-4 text-cyan-400" />
        ) : (
          <ChevronsUpDown className="h-4 w-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </span>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`overflow-hidden rounded-lg border border-slate-800 ${className}`}>
        <TableSkeleton rows={loadingRows} columns={columns.length + (selectable ? 1 : 0)} />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`overflow-hidden rounded-lg border border-slate-800 ${className}`}>
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={emptyIcon}
          action={emptyAction}
        />
      </div>
    );
  }

  const cellPadding = compact ? 'px-3 py-2' : 'px-4 py-3';
  const headerPadding = compact ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <div className={`overflow-hidden rounded-lg border border-slate-800 ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead
            className={`bg-slate-900/50 border-b border-slate-800 ${
              stickyHeader ? 'sticky top-0 z-10' : ''
            }`}
          >
            <tr>
              {selectable && (
                <th className={`${headerPadding} w-12`}>
                  <button
                    onClick={handleSelectAll}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      allSelected
                        ? 'bg-cyan-600 border-cyan-600'
                        : someSelected
                        ? 'bg-slate-700 border-slate-600'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    {allSelected ? (
                      <Check className="h-3 w-3 text-white" />
                    ) : someSelected ? (
                      <Minus className="h-3 w-3 text-white" />
                    ) : null}
                  </button>
                </th>
              )}
              {columns.map((column) => {
                const isSortable = sortable && column.sortable !== false;
                return (
                  <th
                    key={column.id}
                    className={`${headerPadding} text-left text-xs font-semibold text-slate-400 uppercase tracking-wider ${
                      column.headerClassName ?? ''
                    } ${isSortable ? 'cursor-pointer select-none group' : ''}`}
                    style={{ width: column.width }}
                    onClick={isSortable ? () => handleSort(column.id) : undefined}
                  >
                    <div
                      className={`flex items-center ${
                        column.align === 'center'
                          ? 'justify-center'
                          : column.align === 'right'
                          ? 'justify-end'
                          : 'justify-start'
                      }`}
                    >
                      {column.header}
                      {renderSortIcon(column)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {sortedData.map((row, index) => {
              const isSelected = selectedIds.has(row.id);
              const customClassName = rowClassName ? rowClassName(row, index) : '';

              return (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`
                    ${striped && index % 2 === 1 ? 'bg-slate-900/30' : 'bg-transparent'}
                    ${hoverable ? 'hover:bg-slate-800/50' : ''}
                    ${onRowClick ? 'cursor-pointer' : ''}
                    ${isSelected ? 'bg-cyan-900/20' : ''}
                    transition-colors
                    ${customClassName}
                  `}
                >
                  {selectable && (
                    <td className={`${cellPadding} w-12`}>
                      <button
                        onClick={(e) => handleSelectRow(row.id, e)}
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-cyan-600 border-cyan-600'
                            : 'border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </button>
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`${cellPadding} text-sm text-slate-300 ${column.className ?? ''} ${
                        column.align === 'center'
                          ? 'text-center'
                          : column.align === 'right'
                          ? 'text-right'
                          : 'text-left'
                      }`}
                    >
                      {getCellValue(row, column, index)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
